"use client"

import React, { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Initialize theme from localStorage or system preference to prevent flash
  const getInitialTheme = (): Theme => {
    if (typeof window === 'undefined') return 'light'
    
    const savedTheme = localStorage.getItem('theme') as Theme
    if (savedTheme) {
      return savedTheme
    }
    
    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  const [theme, setThemeState] = useState<Theme>(getInitialTheme)

  useEffect(() => {
    // Apply theme immediately on mount
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(theme)
  }, [])

  useEffect(() => {
    console.log('Theme changed to:', theme)
    // Update document class when theme changes
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(theme)
    
    console.log('Document classes after update:', root.classList.toString())
    console.log('Is dark class present:', root.classList.contains('dark'))
    
    // Save theme preference
    localStorage.setItem('theme', theme)
  }, [theme])

  const setTheme = (newTheme: Theme) => {
    console.log('Setting theme to:', newTheme)
    setThemeState(newTheme)
  }

  const toggleTheme = () => {
    console.log('Toggling theme from:', theme)
    setThemeState(prev => prev === 'light' ? 'dark' : 'light')
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
